import React, { useEffect, useMemo, useState, Component } from 'react';

class DialogErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{position:'fixed',top:16,right:16,zIndex:9999,background:'#fee2e2',border:'1px solid #f87171',borderRadius:8,padding:'12px 16px',maxWidth:480,fontSize:13}}>
          <strong>Error en diálogo:</strong> {String(this.state.error?.message || this.state.error)}
          <button onClick={() => this.setState({error:null})} style={{marginLeft:8,cursor:'pointer'}}>✕</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { visitasApi, usuariosApi, exportApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CalendarCheck2, Search, Trash2, Eye, ExternalLink, RefreshCw, Download, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDebounce } from '../hooks/useDebounce';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRow } from '../components/ui/SkeletonRow';
import { formatFecha } from '../utils/dateFormat';
import { VisitaFormDialog } from '../components/visitas/VisitaFormDialog';

const TIPO_LABEL = {
  comercial: 'Comercial',
  tecnica: 'Técnica',
  cortesia: 'Cortesía',
  seguimiento: 'Seguimiento',
  otra: 'Otra',
};
const RESULTADO_STYLE = {
  positivo: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  neutro:   'bg-ink-100 border-ink-200 text-ink-600',
  negativo: 'bg-rose-50 border-rose-200 text-rose-700',
};

const VisitasGlobal = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const verComercial = isAdmin || user?.rol === 'dir_comercial' || user?.rol === 'admin_comercial';

  const [visitas, setVisitas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    q: '', comercial_id: '', tipo: '', resultado: '', desde: '', hasta: '',
  });

  const [confirmDel, setConfirmDel] = useState({ open: false, item: null });
  const [nuevaOpen, setNuevaOpen] = useState(false);

  const qDebounced = useDebounce(filters.q, 300);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.comercial_id) params.comercial_id = filters.comercial_id;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.resultado) params.resultado = filters.resultado;
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      params.limit = 500;
      const list = await visitasApi.list(params);
      setVisitas(Array.isArray(list) ? list : []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      if (verComercial) {
        try { setUsuarios(await usuariosApi.list({ estado: 'activo' })); } catch {}
      }
    })();
    // eslint-disable-next-line
  }, []);

  // eslint-disable-next-line
  useEffect(() => { cargar(); }, [filters.comercial_id, filters.tipo, filters.resultado, filters.desde, filters.hasta]);

  // Filtrado por texto (in-memory) con debounce
  const filtered = useMemo(() => {
    if (!qDebounced) return visitas;
    const qq = qDebounced.toLowerCase();
    return visitas.filter((v) => {
      const hay = `${v.entidad_nombre || ''} ${v.resumen || ''} ${v.siguientes_pasos || ''} ${v.creado_por_nombre || ''}`.toLowerCase();
      return hay.includes(qq);
    });
  }, [visitas, qDebounced]);

  const { sorted, sortKey, sortDir, toggle } = useSortable(
    filtered,
    'fecha',
    'desc',
    {
      empresa: (v) => v.entidad_nombre || '',
      comercial: (v) => v.creado_por_nombre || '',
      tipo: (v) => TIPO_LABEL[v.tipo] || v.tipo || '',
      resultado: (v) => v.resultado || '',
      siguientes: (v) => v.siguientes_pasos || '',
    }
  );

  const pedirBorrar = (v) => setConfirmDel({ open: true, item: v });
  const confirmarBorrar = async () => {
    try {
      await visitasApi.delete(confirmDel.item.id);
      toast.success('Visita eliminada');
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="visitas-global">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-2">
            <CalendarCheck2 className="w-7 h-7 text-brand-600" /> Visitas
          </h1>
          <p className="text-ink-500 mt-1">
            Toda la actividad comercial del equipo: visitas registradas con su tipo, resultado y siguientes pasos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-brand-500 hover:bg-brand-600 text-white"
            size="sm"
            onClick={() => setNuevaOpen(true)}
            data-testid="btn-nueva-visita-global"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nueva visita
          </Button>
          {(user?.rol === 'admin' || user?.rol === 'dir_comercial') && (
            <Button
              variant="outline" size="sm"
              onClick={async () => { try { await exportApi.descargar('visitas'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }}
              data-testid="btn-export-visitas"
            >
              <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-recargar-visitas">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-4 border-ink-200 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <Input
              placeholder="Buscar por empresa, resumen, próximos pasos…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="pl-9"
              data-testid="visitas-filter-q"
            />
          </div>
          {verComercial && (
            <select
              className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
              value={filters.comercial_id}
              onChange={(e) => setFilters({ ...filters, comercial_id: e.target.value })}
              data-testid="visitas-filter-comercial"
            >
              <option value="">Todos los comerciales</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''}</option>)}
            </select>
          )}
          <select
            className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
            value={filters.tipo}
            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            data-testid="visitas-filter-tipo"
          >
            <option value="">Todos los tipos</option>
            {Object.entries(TIPO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select
            className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
            value={filters.resultado}
            onChange={(e) => setFilters({ ...filters, resultado: e.target.value })}
            data-testid="visitas-filter-resultado"
          >
            <option value="">Todos los resultados</option>
            <option value="positivo">Positivo</option>
            <option value="neutro">Neutro</option>
            <option value="negativo">Negativo</option>
          </select>
          <Input
            type="date"
            value={filters.desde}
            onChange={(e) => setFilters({ ...filters, desde: e.target.value })}
            data-testid="visitas-filter-desde"
            placeholder="Desde"
          />
          <Input
            type="date"
            value={filters.hasta}
            onChange={(e) => setFilters({ ...filters, hasta: e.target.value })}
            data-testid="visitas-filter-hasta"
            placeholder="Hasta"
          />
        </div>
      </Card>

      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <SortableHeader label="Fecha"     sortKey="fecha"      currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Empresa"   sortKey="empresa"    currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                {verComercial && (
                  <SortableHeader label="Comercial" sortKey="comercial" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                )}
                <SortableHeader label="Tipo"      sortKey="tipo"       currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Resultado" sortKey="resultado"  currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Siguientes pasos" sortKey="siguientes" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <th className="px-4 py-3 text-right font-semibold text-xs uppercase tracking-wider text-ink-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="visitas-tbody">
              {loading && <SkeletonRow cols={verComercial ? 7 : 6} count={6} />}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={verComercial ? 7 : 6} className="px-0 py-0">
                    <EmptyState
                      compact
                      icon={CalendarCheck2}
                      title="Sin visitas en este rango"
                      description="Ajusta las fechas o el comercial. Puedes registrar una visita desde la ficha del cliente."
                      testid="visitas-empty"
                    />
                  </td>
                </tr>
              )}
              {!loading && sorted.map((v) => (
                <tr key={v.id} className="hover:bg-ink-50" data-testid={`visita-row-${v.id}`}>
                  <td className="px-4 py-3 text-ink-700 font-mono text-xs">{formatFecha(v.fecha)}</td>
                  <td className="px-4 py-3">
                    {v.entidad_id ? (
                      <button
                        onClick={() => navigate(`/clientes/${v.entidad_id}`)}
                        className="text-brand-700 hover:underline font-medium inline-flex items-center gap-1"
                        data-testid={`visita-empresa-${v.id}`}
                      >
                        {v.entidad_nombre || '—'}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    ) : <span className="text-ink-400">—</span>}
                  </td>
                  {verComercial && (
                    <td className="px-4 py-3 text-ink-700">{v.creado_por_nombre || '—'}</td>
                  )}
                  <td className="px-4 py-3">
                    <Badge className="bg-white border border-ink-200 text-ink-700 text-[11px]">
                      {TIPO_LABEL[v.tipo] || v.tipo}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {v.resultado ? (
                      <Badge className={`${RESULTADO_STYLE[v.resultado]} border text-[11px]`}>
                        {v.resultado}
                      </Badge>
                    ) : <span className="text-ink-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-600 max-w-md">
                    <p className="line-clamp-2 text-xs">{v.siguientes_pasos || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => v.entidad_id && navigate(`/clientes/${v.entidad_id}`)}
                        className="p-1.5 rounded hover:bg-ink-100 text-ink-600"
                        title="Ver en ficha de empresa"
                        data-testid={`visita-ver-${v.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => pedirBorrar(v)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                        title="Eliminar"
                        data-testid={`visita-borrar-${v.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="px-4 py-2 text-xs text-ink-500 border-t border-ink-100 bg-ink-50/50">
            Mostrando {sorted.length} {sorted.length === 1 ? 'visita' : 'visitas'}{visitas.length !== sorted.length && ` de ${visitas.length}`}.
          </div>
        )}
      </Card>

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource="visita"
        itemName={confirmDel.item ? `visita del ${confirmDel.item.fecha} a ${confirmDel.item.entidad_nombre || '—'}` : ''}
        description="Esta acción no se puede deshacer. Las tareas derivadas no se eliminarán."
        onConfirm={confirmarBorrar}
        testidPrefix="confirm-del-visita"
      />

      <DialogErrorBoundary>
        <VisitaFormDialog
          open={nuevaOpen}
          onOpenChange={setNuevaOpen}
          showEmpresaSearch={true}
          onSave={async (payload) => {
            await visitasApi.createGlobal(payload);
            toast.success(payload.entidad_id ? 'Visita registrada' : 'Visita registrada (sin empresa asignada)');
            cargar();
          }}
        />
      </DialogErrorBoundary>
    </div>
  );
};

export default VisitasGlobal;
